import {
  call,
  fork,
  getContext,
  put,
  spawn,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import socketIO from 'socket.io-client';
import _omit from 'lodash/omit';
import { getLogger } from '@chedri/base';

import {
  SOCKET_CONNECT_REALTIME_EVENTS,
  SOCKET_CONNECTED_REALTIME_EVENTS,
  SOCKET_DISCONNECTED_REALTIME_EVENTS,
  SOCKET_EVENT_REALTIME_EVENTS,
  REALTIME_EVENTS_SUBSCRIBE,
  REALTIME_EVENTS_UNSUBSCRIBE,
  socketConnectedRealtimeEvents,
  socketDisconnectedRealtimeEvents,
  socketEventRealtimeEvents,
} from 'actions/RealtimeEventActions';

const logger = getLogger('realtime-events');

let eventsSocket = null;

/** Entity rooms requested while connected or before connect (replayed on connect). */
const subscribedEntityRooms = new Set();

function emitEntityRoomSubscribe(rooms) {
  if (!eventsSocket?.connected || !Array.isArray(rooms) || rooms.length === 0) {
    return;
  }

  eventsSocket.emit('subscribe', { rooms });
}

function createEventsSocketChannel(socket) {
  return eventChannel(emit => {
    const dispatchEvent = envelope => {
      emit({ name: 'SocketEvent', eventName: 'event', payload: envelope });
    };

    const connectCallback = () => emit({ name: 'Connected' });
    const disconnectCallback = reason => emit({ name: 'Disconnected', reason });
    const errorCallback = error => emit({ name: 'Error', error });
    const connectErrorCallback = error => emit({ name: 'ConnectError', error });

    socket.on('connect', connectCallback);
    socket.on('disconnect', disconnectCallback);
    socket.on('event', dispatchEvent);
    socket.on('error', errorCallback);
    socket.on('connect_error', connectErrorCallback);

    return () => {
      socket.off('connect', connectCallback);
      socket.off('disconnect', disconnectCallback);
      socket.off('event', dispatchEvent);
      socket.off('error', errorCallback);
      socket.off('connect_error', connectErrorCallback);
    };
  });
}

function* watchEventsSocketEventsSaga(context) {
  context.active = true;
  while (context.active) {
    try {
      const event = yield take(context.channel);
      switch (event.name) {
        case 'SocketEvent':
          if (event.eventName === 'event') {
            yield put(socketEventRealtimeEvents(event.payload));
          }
          break;
        case 'Connected':
          yield put(socketConnectedRealtimeEvents());
          if (subscribedEntityRooms.size > 0) {
            emitEntityRoomSubscribe([...subscribedEntityRooms]);
          }
          break;
        case 'Disconnected':
          yield put(socketDisconnectedRealtimeEvents());
          break;
        case 'Error':
        case 'ConnectError':
          logger.error('Realtime events socket error', event.error);
          break;
        default:
          break;
      }
    } catch (error) {
      logger.error('watchEventsSocketEventsSaga error', error);
    }
  }
}

function hasAuthenticatedUser(user) {
  return Boolean(user && (user.username || user.id || user.logged));
}

function* socketConnectRealtimeEventsSaga() {
  if (eventsSocket?.connected) {
    return;
  }

  if (eventsSocket && !eventsSocket.connected) {
    eventsSocket.connect();
    return;
  }

  try {
    const http = yield getContext('http');
    const baseUrlForSocket = http.defaults.baseURL.replace('/app_dev.php', '');
    const getSocketToken = yield getContext('getSocketToken');
    const socketOptions = yield getContext('socketOptions');
    const token = getSocketToken ? getSocketToken() : null;

    if (!token) {
      logger.warn('Realtime events socket connect skipped: no access token');
      return;
    }

    eventsSocket = socketIO(`${baseUrlForSocket}/events`, {
      path: '/events/socket',
      reconnection: true,
      reconnectionAttempts: 60,
      reconnectionDelay: 10000,
      reconnectionDelayMax: 60000,
      timeout: 20000,
      ...(socketOptions || {}),
      auth: { token },
    });

    const channel = yield call(createEventsSocketChannel, eventsSocket);
    const context = { socket: eventsSocket, active: false, channel };
    yield spawn(watchEventsSocketEventsSaga, context);
  } catch (err) {
    logger.error('Exception while connecting to realtime events socket', err);
  }
}

function* realtimeEventsSubscribeSaga(action) {
  const { rooms } = action.payload || {};
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return;
  }

  rooms.forEach(room => subscribedEntityRooms.add(room));
  emitEntityRoomSubscribe(rooms);
}

function* realtimeEventsUnsubscribeSaga(action) {
  const { rooms } = action.payload || {};
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return;
  }

  rooms.forEach(room => subscribedEntityRooms.delete(room));
  if (eventsSocket?.connected) {
    eventsSocket.emit('unsubscribe', { rooms });
  }
}

function* connectRealtimeEventsAfterAuth(action) {
  const user = action?.payload?.data;
  if (!hasAuthenticatedUser(user)) {
    return;
  }

  yield call(socketConnectRealtimeEventsSaga);
}

function* connectRealtimeEventsAfterLogin() {
  yield call(socketConnectRealtimeEventsSaga);
}

export function* watchRealtimeEventActions() {
  yield takeEvery(SOCKET_CONNECT_REALTIME_EVENTS, socketConnectRealtimeEventsSaga);
  yield takeEvery('FETCH_CREDENTIALS_FULFILLED', connectRealtimeEventsAfterAuth);
  yield takeEvery('HANDLE_LOGIN_FULFILLED', connectRealtimeEventsAfterLogin);
  yield takeEvery(REALTIME_EVENTS_SUBSCRIBE, realtimeEventsSubscribeSaga);
  yield takeEvery(REALTIME_EVENTS_UNSUBSCRIBE, realtimeEventsUnsubscribeSaga);
}

export default [watchRealtimeEventActions];
